import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Box, Stack } from '@mui/system';
import { useQuery } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { SendInvoiceToPatientDialog, SendStatementToPatientDialog } from 'src/components/dialogs';
import {
  chooseJson,
  EXPORT_INVOICES_ZAMBDA_KEY,
  ExportInvoicesCsvKickOffResponse,
  ExportInvoicesCsvStatusResponse,
  ExportInvoicesTasksCsvInput,
  formatDateConfigurable,
  GET_INVOICES_TASKS_ZAMBDA_KEY,
  GetInvoicesTasksInput,
  GetInvoicesTasksResponse,
  getLatestTaskOutput,
  INVOICEABLE_PATIENTS_PAGE_SIZE,
  InvoiceablePatientReport,
  InvoiceSortDirection,
  InvoiceSortDirectionValues,
  InvoiceSortField,
  InvoiceSortFieldValues,
  InvoiceTaskDisplayStatus,
  InvoiceTaskDisplayStatuses,
  InvoiceTaskInput,
  mapDisplayToInvoiceTaskStatus,
  mapInvoiceTaskStatusToDisplay,
} from 'utils';
import { updateInvoiceTask } from '../../api/api';
import { GenericToolTip } from '../../components/GenericToolTip';
import { SelectInput } from '../../components/input/SelectInput';
import { MappedStatusChip } from '../../components/MappedStatusChip';
import { useApiClients } from '../../hooks/useAppClients';
import PageContainer from '../../layout/PageContainer';

const LOCAL_STORAGE_FILTERS_KEY = 'invoices-tasks.filters';

const SP = {
  page: 'page',
  status: 'status',
  patient: 'patient',
  sortField: 'sortField',
  sortDirection: 'sortDirection',
  hideZeroBalance: 'hideZeroBalance',
} as const;

const INVOICEABLE_TASK_STATUS_COLORS_MAP: {
  [status in InvoiceTaskDisplayStatus]: {
    background: {
      primary: string;
      secondary?: string;
    };
    color: {
      primary: string;
      secondary?: string;
    };
  };
} = {
  ready: {
    background: {
      primary: '#6129ef',
    },
    color: {
      primary: '#ffffff',
    },
  },
  updating: {
    background: {
      primary: '#B3E5FC',
    },
    color: {
      primary: '#01579B',
    },
  },
  sending: {
    background: {
      primary: '#D1C4E9',
    },
    color: {
      primary: '#311B92',
    },
  },
  sent: {
    background: {
      primary: '#C8E6C9',
    },
    color: {
      primary: '#1B5E20',
    },
  },
  error: {
    background: {
      primary: '#FFCCBC',
    },
    color: {
      primary: '#BF360C',
    },
  },
};

export default function InvoiceablePatients(): React.ReactElement {
  const { oystehrZambda } = useApiClients();
  const navigate = useNavigate();
  const methods = useForm();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedReportToSend, setSelectedReportToSend] = useState<InvoiceablePatientReport | undefined>();
  const [selectedReportForStatement, setSelectedReportForStatement] = useState<InvoiceablePatientReport | undefined>();
  const [updatingTaskIds, setUpdatingTaskIds] = useState<Set<string>>(new Set());
  const [sendingTaskIds, setSendingTaskIds] = useState<Set<string>>(new Set());
  const [isExporting, setIsExporting] = useState(false);

  const pageParam = searchParams.get(SP.page);
  const parsedPage = pageParam ? parseInt(pageParam, 10) : 0;
  const pageSP = Number.isFinite(parsedPage) && parsedPage >= 0 ? parsedPage : 0;
  const [pageInputValue, setPageInputValue] = useState(String(pageSP + 1));
  const statusSP = searchParams.get(SP.status);
  const patientSP = searchParams.get(SP.patient);
  const sortFieldSP =
    (searchParams.get(SP.sortField) as InvoiceSortField | null) ?? InvoiceSortFieldValues.finalizationDate;
  const sortDirectionSP =
    (searchParams.get(SP.sortDirection) as InvoiceSortDirection | null) ?? InvoiceSortDirectionValues.desc;
  const hideZeroBalanceSP = searchParams.get(SP.hideZeroBalance) !== 'false';
  const {
    data: invoiceablePatients,
    isLoading: isInvoiceablePatientsLoading,
    refetch: refetchInvoiceablePatients,
  } = useQuery<GetInvoicesTasksResponse>({
    queryKey: [
      GET_INVOICES_TASKS_ZAMBDA_KEY,
      pageSP,
      statusSP,
      patientSP,
      sortFieldSP,
      sortDirectionSP,
      hideZeroBalanceSP,
    ],
    queryFn: async () => {
      if (!oystehrZambda) throw new Error('oystehrZambda not defined');
      const params: GetInvoicesTasksInput = {
        page: pageSP,
        status: statusSP ? mapDisplayToInvoiceTaskStatus(statusSP as InvoiceTaskDisplayStatus) : undefined,
        patientId: patientSP ?? undefined,
        sortField: sortFieldSP,
        sortDirection: sortDirectionSP,
        hideZeroBalance: hideZeroBalanceSP,
      };
      const response = await oystehrZambda.zambda.execute({
        id: GET_INVOICES_TASKS_ZAMBDA_KEY,
        ...params,
      });
      return chooseJson(response);
    },
    enabled: oystehrZambda !== undefined,
    retry: 2,
    staleTime: 5 * 1000,
    refetchInterval: 5 * 1000,
  });

  const totalPages = Math.ceil((invoiceablePatients?.totalCount ?? 0) / INVOICEABLE_PATIENTS_PAGE_SIZE);

  const handleBack = (): void => {
    navigate('/reports');
  };

  const setPage = (page: number): void => {
    searchParams.set(SP.page, page.toString());
    setSearchParams(searchParams);
  };

  const handlePageJump = (
    e: React.KeyboardEvent<HTMLDivElement> | React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    if ('key' in e && e.key !== 'Enter') return;
    if (totalPages === 0) return;

    let targetPage = parseInt(pageInputValue, 10);

    if (isNaN(targetPage) || targetPage < 1) {
      targetPage = 1;
    } else if (targetPage > totalPages) {
      targetPage = totalPages;
    }

    setPageInputValue(String(targetPage));

    if (targetPage - 1 !== pageSP) {
      setPage(targetPage - 1);
    }
  };

  const setSortField = (field: InvoiceSortField): void => {
    if (field === sortFieldSP) {
      searchParams.set(
        SP.sortDirection,
        sortDirectionSP === InvoiceSortDirectionValues.desc
          ? InvoiceSortDirectionValues.asc
          : InvoiceSortDirectionValues.desc
      );
    } else {
      searchParams.set(SP.sortField, field);
      searchParams.set(SP.sortDirection, InvoiceSortDirectionValues.desc);
    }
    searchParams.set(SP.page, '0');
    setSearchParams(searchParams);
  };

  const sendInvoice = async (taskId: string, invoiceTaskInput: InvoiceTaskInput): Promise<void> => {
    try {
      if (oystehrZambda) {
        setSendingTaskIds((prev) => new Set(prev).add(taskId));

        await updateInvoiceTask(oystehrZambda, {
          taskId,
          status: mapDisplayToInvoiceTaskStatus('sending'),
          invoiceTaskInput,
        }).finally(() => {
          setSendingTaskIds((prev) => {
            const next = new Set(prev);
            next.delete(taskId);
            return next;
          });
        });

        setSelectedReportToSend(undefined);
        enqueueSnackbar('Invoice status changed to "sending"', { variant: 'success' });
        await refetchInvoiceablePatients();
      }
    } catch {
      enqueueSnackbar('Error occurred, please try again', { variant: 'error' });
    }
  };

  const updateInvoice = (taskId: string | undefined): void => {
    if (!oystehrZambda || !taskId) return;

    setUpdatingTaskIds((prev) => new Set(prev).add(taskId));

    updateInvoiceTask(oystehrZambda, {
      taskId,
      status: mapDisplayToInvoiceTaskStatus('updating'),
    })
      .then(async () => {
        enqueueSnackbar('Invoice status changed to "updating"', { variant: 'success' });
        await refetchInvoiceablePatients();
      })
      .catch(() => {
        enqueueSnackbar('Error occurred, please try again', { variant: 'error' });
      })
      .finally(() => {
        setUpdatingTaskIds((prev) => {
          const next = new Set(prev);
          next.delete(taskId);
          return next;
        });
      });
  };

  const handleExportCsv = async (): Promise<void> => {
    if (!oystehrZambda) return;
    setIsExporting(true);
    try {
      // Step 1: Kick off export by creating a Task
      const params: ExportInvoicesTasksCsvInput = {
        status: statusSP ? mapDisplayToInvoiceTaskStatus(statusSP as InvoiceTaskDisplayStatus) : undefined,
        sortField: sortFieldSP,
        sortDirection: sortDirectionSP,
        hideZeroBalance: hideZeroBalanceSP,
      };
      const kickOffResponse = await oystehrZambda.zambda.execute({
        id: EXPORT_INVOICES_ZAMBDA_KEY,
        ...params,
      });
      const { taskId } = chooseJson(kickOffResponse) as ExportInvoicesCsvKickOffResponse;

      // Step 2: Poll for completion
      const POLL_INTERVAL_MS = 2000;
      const MAX_POLLS = 150; // 5 minutes max
      let polls = 0;

      const pollForResult = async (): Promise<ExportInvoicesCsvStatusResponse> => {
        while (polls < MAX_POLLS) {
          polls++;
          await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
          const statusResponse = await oystehrZambda.zambda.execute({
            id: EXPORT_INVOICES_ZAMBDA_KEY,
            taskId,
          });
          const statusData = chooseJson(statusResponse) as ExportInvoicesCsvStatusResponse;

          if (statusData.status === 'completed' || statusData.status === 'failed') {
            return statusData;
          }
        }
        throw new Error('Export timed out');
      };

      const result = await pollForResult();

      if (result.status === 'completed' && result.downloadUrl) {
        // Step 3: Download the CSV via the presigned URL
        const csvResponse = await fetch(result.downloadUrl);
        if (!csvResponse.ok) {
          throw new Error(`Failed to download CSV: ${csvResponse.status}`);
        }
        const csvText = await csvResponse.text();
        const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `invoiceable-patients-report.csv`;
        link.click();
        URL.revokeObjectURL(url);
      } else {
        enqueueSnackbar(result.error ?? 'Export failed', { variant: 'error' });
      }
    } catch {
      enqueueSnackbar('Failed to export CSV', { variant: 'error' });
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    setPageInputValue(String(pageSP + 1));
  }, [pageSP]);

  useEffect(() => {
    const filtersValues = {
      status: searchParams.get(SP.status),
      patient: searchParams.get(SP.patient),
    };
    methods.reset(filtersValues);
  }, [searchParams, methods]);

  useEffect(() => {
    const callback = methods.subscribe({
      formState: {
        values: true,
      },
      callback: ({ values }) => {
        const queryParams = new URLSearchParams();
        const filtersToPersist: Record<string, string> = {};
        for (const key in values) {
          const value = values[key];
          if (value) {
            queryParams.set(key, value);
            filtersToPersist[key] = value;
          }
        }
        // Preserve sort and hideZeroBalance params — they are managed separately from the form
        if (searchParams.has(SP.sortField)) queryParams.set(SP.sortField, searchParams.get(SP.sortField)!);
        if (searchParams.has(SP.sortDirection)) queryParams.set(SP.sortDirection, searchParams.get(SP.sortDirection)!);
        if (searchParams.has(SP.hideZeroBalance))
          queryParams.set(SP.hideZeroBalance, searchParams.get(SP.hideZeroBalance)!);
        setSearchParams(queryParams);
        if (Object.keys(filtersToPersist).length > 0) {
          localStorage.setItem(LOCAL_STORAGE_FILTERS_KEY, JSON.stringify(filtersToPersist));
        } else {
          localStorage.removeItem(LOCAL_STORAGE_FILTERS_KEY);
        }
      },
    });
    return () => callback();
  }, [methods, navigate, searchParams, setSearchParams]);

  useEffect(() => {
    const persistedFilters = localStorage.getItem(LOCAL_STORAGE_FILTERS_KEY);
    if (searchParams.size === 0) {
      const queryParams = new URLSearchParams();
      if (persistedFilters != null) {
        const filters = JSON.parse(persistedFilters) as Record<string, string>;
        for (const key in filters) {
          if (key && filters[key]) queryParams.set(key, filters[key]);
        }
      }
      if (!queryParams.has(SP.sortField)) queryParams.set(SP.sortField, InvoiceSortFieldValues.finalizationDate);
      if (!queryParams.has(SP.sortDirection)) queryParams.set(SP.sortDirection, InvoiceSortDirectionValues.desc);
      if (!queryParams.has(SP.hideZeroBalance)) queryParams.set(SP.hideZeroBalance, 'true');
      setSearchParams(queryParams);
    }
  }, [searchParams, setSearchParams]);

  return (
    <PageContainer>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={handleBack} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AssessmentIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4" component="h1" color="primary.dark" fontWeight={600}>
              Invoiceable Patients Report
            </Typography>
          </Box>
        </Box>

        <FormProvider {...methods}>
          <Paper>
            <Stack direction="row" spacing={2} padding="8px" alignItems="center" flexWrap="wrap">
              <Box sx={{ width: '30%' }}>
                <SelectInput name="status" label="Status" options={InvoiceTaskDisplayStatuses as unknown as string[]} />
              </Box>
              <TextField
                {...methods.register('patient')}
                label="Patient id"
                sx={{ width: '30%', flex: 1 }}
                size="small"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={hideZeroBalanceSP}
                    onChange={(e) => {
                      searchParams.set(SP.hideZeroBalance, e.target.checked ? 'true' : 'false');
                      searchParams.set(SP.page, '0');
                      setSearchParams(searchParams);
                    }}
                  />
                }
                label="Hide $0 balances"
                sx={{ whiteSpace: 'nowrap' }}
              />
              <Button
                variant="text"
                size="small"
                startIcon={isExporting ? <CircularProgress size={16} /> : <SaveAltIcon />}
                sx={{ textTransform: 'uppercase', whiteSpace: 'nowrap', ml: 'auto' }}
                disabled={isExporting}
                onClick={handleExportCsv}
              >
                Export
              </Button>
            </Stack>
          </Paper>
        </FormProvider>
        <Paper sx={{ mt: 2 }}>
          <Table sx={{ width: '100%' }}>
            <TableHead>
              <TableRow>
                <TableCell style={{ width: '200px' }}>
                  <Typography fontWeight="500" fontSize="14px">
                    Patient Name
                  </Typography>
                </TableCell>
                <TableCell style={{ width: '150px' }}>
                  <TableSortLabel
                    active={sortFieldSP === InvoiceSortFieldValues.appointmentDate}
                    direction={
                      sortFieldSP === InvoiceSortFieldValues.appointmentDate
                        ? sortDirectionSP
                        : InvoiceSortDirectionValues.desc
                    }
                    onClick={() => setSortField(InvoiceSortFieldValues.appointmentDate)}
                  >
                    <Typography fontWeight="500" fontSize="14px">
                      Date of Service
                    </Typography>
                  </TableSortLabel>
                </TableCell>
                <TableCell style={{ width: '150px' }}>
                  <TableSortLabel
                    active={sortFieldSP === InvoiceSortFieldValues.finalizationDate}
                    direction={
                      sortFieldSP === InvoiceSortFieldValues.finalizationDate
                        ? sortDirectionSP
                        : InvoiceSortDirectionValues.desc
                    }
                    onClick={() => setSortField(InvoiceSortFieldValues.finalizationDate)}
                  >
                    <Typography fontWeight="500" fontSize="14px">
                      Finalization Date
                    </Typography>
                  </TableSortLabel>
                </TableCell>
                <TableCell style={{ width: '120px' }}>
                  <Typography fontWeight="500" fontSize="14px">
                    Amount
                  </Typography>
                </TableCell>
                <TableCell style={{ width: '100px' }}>
                  <Typography fontWeight="500" fontSize="14px">
                    Invoice Status
                  </Typography>
                </TableCell>
                <TableCell style={{ width: '200px' }}>
                  <Typography fontWeight="500" fontSize="14px">
                    Actions
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isInvoiceablePatientsLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : null}
              {!isInvoiceablePatientsLoading && (invoiceablePatients?.reports ?? []).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2">No reports</Typography>
                  </TableCell>
                </TableRow>
              ) : null}
              {!isInvoiceablePatientsLoading &&
                (invoiceablePatients?.reports ?? []).map((report) => {
                  const isUpdating = report.task.id ? updatingTaskIds.has(report.task.id) : false;
                  const isSending = report.task.id ? sendingTaskIds.has(report.task.id) : false;
                  const displayStatus = isUpdating
                    ? 'updating'
                    : isSending
                    ? 'sending'
                    : mapInvoiceTaskStatusToDisplay(report.task.status);
                  const lastTaskOutput = getLatestTaskOutput(report.task);
                  const statusTooltipMessage = lastTaskOutput
                    ? lastTaskOutput.type === 'success'
                      ? 'Invoice id: ' + lastTaskOutput.message
                      : 'Error: ' + lastTaskOutput.message
                    : displayStatus;
                  const maskedClaimId =
                    report.claimId.length > 12
                      ? `${report.claimId.slice(0, 6)}...${report.claimId.slice(-4)}`
                      : report.claimId;

                  return (
                    <TableRow key={report.task.id}>
                      <TableCell>
                        <GenericToolTip
                          customWidth={340}
                          title={
                            <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  DOB
                                </Typography>
                                <Typography variant="body2">{report.patient.dob ?? '---'}</Typography>
                              </Box>
                              <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  Responsible Party
                                </Typography>
                                <Typography variant="body2">{report.responsibleParty.fullName ?? '---'}</Typography>
                                <Typography variant="body2">
                                  {report.responsibleParty.relationshipToPatient ?? '---'}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  RCM Claim ID
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                    {maskedClaimId}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    onClick={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      void navigator.clipboard
                                        .writeText(report.claimId)
                                        .then(() => enqueueSnackbar('Copied to clipboard', { variant: 'success' }));
                                    }}
                                  >
                                    <ContentCopyIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Box>
                            </Box>
                          }
                        >
                          <Box sx={{ display: 'inline-flex' }}>
                            <Link
                              to={`/patient/${report.patient.patientId}`}
                              style={{ textDecoration: 'underline', color: 'inherit' }}
                            >
                              <Typography variant="inherit">{report.patient.fullName}</Typography>
                            </Link>
                          </Box>
                        </GenericToolTip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1">{report.visitDate}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1">
                          {formatDateConfigurable({ isoDate: report.finalizationDateISO })}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1">${(report.amountInvoiceable / 100).toFixed(2)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={`${statusTooltipMessage} (click to copy)`}>
                          <span
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              void navigator.clipboard
                                .writeText(statusTooltipMessage)
                                .then(() => enqueueSnackbar('Status copied to clipboard', { variant: 'success' }));
                            }}
                            style={{ cursor: 'pointer' }}
                          >
                            <MappedStatusChip status={displayStatus} mapper={INVOICEABLE_TASK_STATUS_COLORS_MAP} />
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Button
                          sx={{ mr: 1 }}
                          onClick={() => {
                            updateInvoice(report.task.id);
                          }}
                        >
                          Refresh
                        </Button>
                        <Button
                          disabled={
                            isUpdating || isSending || displayStatus === 'updating' || displayStatus === 'sending'
                          }
                          onClick={() => {
                            setSelectedReportToSend(report);
                          }}
                          variant="contained"
                        >
                          Invoice
                        </Button>
                        <Button
                          sx={{ ml: 1 }}
                          variant="contained"
                          disabled={
                            isUpdating || isSending || displayStatus === 'updating' || displayStatus === 'sending'
                          }
                          onClick={() => {
                            setSelectedReportForStatement(report);
                          }}
                        >
                          Statement
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', pr: 2 }}>
            <TablePagination
              rowsPerPageOptions={[INVOICEABLE_PATIENTS_PAGE_SIZE]}
              component="div"
              count={invoiceablePatients?.totalCount ?? -1}
              rowsPerPage={INVOICEABLE_PATIENTS_PAGE_SIZE}
              page={pageSP}
              onPageChange={(_e, newPageNumber) => {
                setPage(newPageNumber);
              }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                Go to page
              </Typography>
              <TextField
                size="small"
                value={pageInputValue}
                onChange={(e) => setPageInputValue(e.target.value)}
                onKeyDown={handlePageJump}
                onBlur={handlePageJump}
                disabled={isInvoiceablePatientsLoading || totalPages <= 1}
                inputProps={{
                  style: { padding: '4px 8px', width: '35px', textAlign: 'center' },
                  'aria-label': 'Go to page',
                }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                of {totalPages}
              </Typography>
            </Box>
          </Box>
        </Paper>
        <SendInvoiceToPatientDialog
          title="Send invoice"
          modalOpen={selectedReportToSend !== undefined}
          handleClose={() => {
            setSelectedReportToSend(undefined);
          }}
          submitButtonName="Send Invoice"
          onSubmit={sendInvoice}
          report={selectedReportToSend}
        />
        <SendStatementToPatientDialog
          modalOpen={selectedReportForStatement !== undefined}
          handleClose={() => {
            setSelectedReportForStatement(undefined);
          }}
          onSubmit={() => {
            // TODO: implement send statement
            setSelectedReportForStatement(undefined);
          }}
          report={selectedReportForStatement}
        />
      </Box>
    </PageContainer>
  );
}
